import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import moment from 'moment';
import { ModalController } from '@ionic/angular';
import { ActivatedRoute, Router } from '@angular/router';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { HomePage } from '../home/home.page';
import { Api } from '../../../smoothr-web-app-core/api/api';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import { environment } from '../../../environments/environment';
import { AnalyticsService } from '../../../smoothr-web-app-core/services/analytics/analytics.service';
import { firstOfObservable, numberD, numberToCurrency, sleep } from '../../../smoothr-web-app-core/utils/utils';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import MilesAndMore from 'src/smoothr-web-app-core/models/MilesAndMore';
import { RequestEmailComponent } from './request-email/request-email.component';
import Payment from 'src/smoothr-web-app-core/models/Payment';
import { timer, throwError, Subject } from 'rxjs';
import { switchMap, tap, takeWhile, catchError, takeUntil } from 'rxjs/operators';

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from '../../../assets/pdfmake/vfs_fonts/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { FailLoadingReceiptComponent } from './fail-loading-receipt/fail-loading-receipt.component';
import { OrderStatus } from 'src/smoothr-web-app-core/enums/OrderStatus';
import { Socket } from 'ngx-socket-io';
import { SocketRole } from 'src/smoothr-web-app-core/models/SocketRoles';
import { AwaitNotificationModalComponent } from './await-notification-modal/await-notification-modal.component';

pdfMake.vfs = pdfFonts;

@Component({
	selector: 'app-payment-success',
	templateUrl: './payment-success.page.html',
	styleUrls: ['payment-success.page.scss'],
	standalone: false,
})
export class PaymentSuccessPage extends RepositoryDirective implements OnInit, OnDestroy {
	static url = 'payment/success';
	static urlWithPaymentParam = 'payment/success/:payment';
	static ORDER_ID_PARAM = 'order';
	static notification_key = 'payment_success_page_notification_was_shown';
	private destroy$ = new Subject<void>();

	moment = moment;
	OrderUtils = OrderUtils;
	pt = PreorderType;
	orderVenue: Venue;
	error: any;
	orderId: string;
	environment = environment;
	numberToCurrency = numberToCurrency;
	loading = true;
	tip: number = 0;
	milesAndMore: MilesAndMore | null = null;
	payment: Payment;
	public orderStatusEnum = OrderStatus;
	private isOpenFailModal = false;

	private translate = inject(TranslateService);
	protected repository = inject(RepositoryService);
	private router = inject(Router);
	private snackbarCtrl = inject(MatSnackBar);
	private route = inject(ActivatedRoute);
	private analytics = inject(AnalyticsService);
	private modalCtrl = inject(ModalController);
	private socket = inject(Socket);

	constructor() {
		super();
	}

	static async navigate(router: Router, order: string[], payment: string) {
		await router.navigate([this.url + '/' + payment], {
			queryParams: {
				order,
			},
		});
	}

	ionViewDidEnter() {
		console.log(this.verifiedOrder);
		this.repository.order.emit(null);
		if (!this.loading) {
			this.reloadOrder();
		}
	}

	ionViewWillLeave() {
		this.destroy$.next();
		this.destroy$.complete();

		this.socket.removeAllListeners();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();

		this.socket.removeAllListeners();
	}

	async onInitFinish() {
		super.onInitFinish();
		this.route.queryParams.subscribe(params => {
			console.log({
				params,
			});
			if (this.verifiedOrder && this.verifiedOrder._id && !params.order) {
				this.orderId = this.verifiedOrder._id;
			} else {
				this.orderId = params.order;
			}
			this.reloadOrder();
		});

		this.showNotificationModal();
	}

	async reloadOrder(attempt: number = 0, error = null) {
		this.error = null;
		this.loading = true;
		if (attempt > 10) {
			this.error = error ? error : this.translate.instant('payment_success_page.payment_error');
			this.snackbarCtrl.open(error, null, {
				duration: 2000,
			});
			this.loading = false;
			return;
		}
		if (attempt > 0) {
			await sleep(200);
		}
		try {
			const orderResponse = await Api.getOrder(this.orderId);
			this.repository.verifiedOrder.emit(orderResponse.data);
			if (!this.verifiedOrder) {
				await this.reloadOrder(attempt + 1, this.translate.instant('payment_success_page.please_reload'));
				return;
			}
			const params = await firstOfObservable(this.route.params);
			if (params.payment) {
				const waitForReceipt$ = this.waitForReceipt$(params.payment)
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						complete: async () => {
							if (this.payment && !this.payment.posCheckoutReceipt && !this.isOpenFailModal) {
								waitForReceipt$.unsubscribe();
								this.isOpenFailModal = true;
								// this.sendRequestEmailTemplateWithReceipt();
								const title = this.translate.instant('await_notification.title1');
								const message = this.translate.instant('await_notification.message1');

								this.modalCtrl.dismiss();

								await AwaitNotificationModalComponent.show(this.modalCtrl, title, message, 30);

								FailLoadingReceiptComponent.show(this.modalCtrl);
							}
						},
						error: err => {
							console.error('Polling error:', err);
						},
					});
			}
			this.analytics.paymentSuccess();
			try {
				if (this.venue && this.venue._id === this.verifiedOrder.venue) {
					this.orderVenue = this.venue;
				} else {
					this.orderVenue = (await Api.getLazyVenue(this.verifiedOrder.venue)).data;
				}
				// if (this.verifiedOrder.status !== OrderStatus.DONE) {
				// 	this.listenSocketOrders(this.orderVenue._id);
				// }

				this.error = false;
				this.loading = false;
			} catch (e) {
				console.error(e);
				await this.reloadOrder(attempt + 1, error ? error : e);
				return;
			}
		} catch (e) {
			console.error(e);
			await this.reloadOrder(attempt + 1, e);
			return;
		}
	}

	home() {
		if (this.loading) {
			return;
		}
		if (this.error) {
			this.reloadOrder();
			return;
		}
		this.loading = true;
		HomePage.navigate(this.router, this.repository);
	}

	openReviewHyperlink(venue: Venue) {
		try {
			console.log(venue);
			const url = (venue as Venue & { reviewHyperlink: string }).reviewHyperlink;

			window.open(url, '_blank');
		} catch (error) {
			console.log(error);
		}
	}

	async requestEmail() {
		await RequestEmailComponent.show(this.modalCtrl, this.payment, this.orderVenue);
	}

	waitForReceipt$(paymentId: string) {
		console.log('here');

		const intervalMs = 10_000; // every 10 sec
		const timeoutMs = 1 * 30 * 1_000; // 1 min
		const startTime = Date.now();

		return timer(0, intervalMs).pipe(
			takeUntil(this.destroy$),
			switchMap(() => Api.getPayment(paymentId).then(res => res.data)),
			tap(payment => {
				this.payment = payment;
				this.tip = numberD(payment.tip);
				this.milesAndMore = payment.milesAndMore;
			}),
			takeWhile(payment => {
				const receiptExists = !!payment.posCheckoutReceipt;
				const timeoutReached = Date.now() - startTime > timeoutMs;
				return !receiptExists && !timeoutReached;
			}, true),
			catchError(err => {
				console.error('Polling failed:', err);
				return throwError(() => new Error('Failed to retrieve receipt'));
			})
		);
	}

	generatePdf() {
		const receiptText = this.payment?.posCheckoutReceipt;

		if (!receiptText) {
			return;
		}
		pdfMake.fonts = {
			Courier: {
				normal: 'CourierPrime-Regular.ttf',
				bold: 'CourierPrime-Bold.ttf',
				italics: 'CourierPrime-Italic.ttf',
				bolditalics: 'CourierPrime-BoldItalic.ttf',
			},
		};

		const docDefinition: TDocumentDefinitions = {
			content: [
				{
					columns: [
						{
							// auto-sized columns have their widths based on their content
							width: '*',
							text: '',
						},
						{
							width: 'auto',
							text: receiptText,
							alignment: 'center',
						},
						{
							width: '*',
							text: '',
						},
					],
					fontSize: 9,
					font: 'Courier',
					lineHeight: 1,
				},
			],
			pageSize: 'A5',
			pageMargins: [10, 10, 10, 10],
		};

		const pdfDoc = pdfMake.createPdf(docDefinition);

		const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

		if (isFirefox) {
			pdfDoc.download('receipt.pdf');
		} else {
			pdfMake.createPdf(docDefinition).getBlob(blob => {
				const url = URL.createObjectURL(blob);

				window.open(url, '_blank');

				setTimeout(() => URL.revokeObjectURL(url), 1000);
			});
		}
	}

	async sendRequestEmailTemplateWithReceipt() {
		const email = this.verifiedOrder?.preorder?.email;
		if (!email) {
			return;
		}

		try {
			const response = Api.sendCustomerInvoiceEmail(
				{ email: email.trim(), invoiceMailType: 'receipt' },
				this.payment._id,
				this.translate.currentLang
			);
		} catch (e) {
			this.snackbarCtrl.open('Sorry, we have a problem with sending email...', null, {
				duration: 5000,
			});
			console.log(e);
		}
	}

	private authenticateSocket(data: { roles: SocketRole[]; venueIds: string[] }): void {
		this.socket.emit('authenticate', data.roles, data.venueIds, (ack: any) => {
			console.log('Socket authenticated for ', ack, data);
		});
	}

	private listenSocketOrders(venueId: string) {
		const connectEvent = 'connect';
		const orderEvent = 'venue-' + venueId + '-orders';

		if (this.socket.connected) {
			this.authenticateSocket({
				roles: [SocketRole.COUNTER_DISPLAY, SocketRole.COUNTER_DISPLAY_MAIN],
				venueIds: [venueId],
			});
		} else {
			// fallback якщо ще не підключений
			this.socket.once(connectEvent, () => {
				this.authenticateSocket({
					roles: [SocketRole.COUNTER_DISPLAY, SocketRole.COUNTER_DISPLAY_MAIN],
					venueIds: [venueId],
				});
			});
		}

		const orderHandler = (data: any) => {
			if (data?.order && (data?.type == 'update' || data?.type == 'created')) {
				console.log('LISTEN', data);
				if (data?.order._id === this.verifiedOrder._id) {
					this.repository.verifiedOrder.emit(data.order);
				}
				if (data.order.status === OrderStatus.DONE) {
					this.socket.removeAllListeners();
				}
			}
		};

		this.socket.on(orderEvent, orderHandler);
	}

	async showNotificationModal() {
		const shown = JSON.parse(localStorage.getItem(PaymentSuccessPage.notification_key) ?? 'false');

		if (shown) {
			return;
		}

		localStorage.setItem(PaymentSuccessPage.notification_key, JSON.stringify(true));

		await sleep(100);
		const title = this.translate.instant('await_notification.title');
		const message = this.translate.instant('await_notification.message');

		AwaitNotificationModalComponent.show(this.modalCtrl, title, message);
	}
}
